import { Routes } from '@angular/router';
import { anonGuard } from '@taxora-business-auth/data-access/guards/anon-guard';
import { authGuard } from '@taxora-business-auth/data-access/guards/auth-guard';
import { checkEmailGuard } from '@taxora-business-auth/data-access/guards/check-email-guard';
import { AuthState } from '@taxora-business-auth/data-access/state/auth-state';
import { invitationAuthGuard } from '@taxora-business-invitations/data-access/guards/invitation-auth-guard';
import { AuthenticatedLayout } from '@taxora-business-core/feature/authenticated-layout/authenticated-layout';
import { MainLayout } from '@taxora-business-core/feature/main-layout/main-layout';
import { APP_ROUTES } from '@taxora-business-core/util/constants/app-routes.constant';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: APP_ROUTES.DASHBOARD },
  {
    path: '',
    canActivate: [anonGuard],
    component: AuthenticatedLayout,
    children: [
      { path: '', pathMatch: 'full', redirectTo: APP_ROUTES.DASHBOARD },
      {
        path: APP_ROUTES.DASHBOARD,
        loadComponent: () =>
          import('@taxora-business-dashboard/feature/dashboard-page/dashboard-page'),
      },
      {
        path: APP_ROUTES.ORGANIZATION.ROOT,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@taxora-business-organization/feature/organization-details-page/organization-details-page').then(
                (m) => m.OrganizationDetailsPage,
              ),
          },
          {
            path: APP_ROUTES.ORGANIZATION.CREATE,
            loadComponent: () =>
              import('@taxora-business-organization/feature/organization-create-page/organization-create-page').then(
                (m) => m.OrganizationCreatePage,
              ),
          },
          {
            path: APP_ROUTES.ORGANIZATION.EDIT,
            loadComponent: () =>
              import('@taxora-business-organization/feature/organization-edit-page/organization-edit-page').then(
                (m) => m.OrganizationEditPage,
              ),
          },
        ],
      },
      {
        path: APP_ROUTES.INVITATIONS.ROOT,
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('@taxora-business-invitations/feature/invitations-page/invitations-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.DOCUMENTS,
        loadComponent: () =>
          import('../documents/feature/documents-page/documents-page').then((m) => m.DocumentsPage),
      },
      {
        path: APP_ROUTES.PROVIDERS.ROOT,
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('../providers/feature/providers-page/providers-page').then(
                (m) => m.ProvidersPage,
              ),
          },
          {
            path: APP_ROUTES.PROVIDERS.WORKSPACE,
            loadComponent: () =>
              import('../providers/feature/provider-workspace-page/provider-workspace-page').then(
                (m) => m.ProviderWorkspacePage,
              ),
          },
        ],
      },
    ],
  },
  {
    path: `${APP_ROUTES.INVITATIONS.ROOT}/${APP_ROUTES.INVITATIONS.ACCEPT.ROOT}/${APP_ROUTES.INVITATIONS.ACCEPT.TOKEN}`,
    canActivate: [invitationAuthGuard],
    loadComponent: () =>
      import('@taxora-business-invitations/feature/invitations-page/invitations-page'),
  },
  {
    path: APP_ROUTES.AUTH.ROOT,
    canActivate: [authGuard],
    component: MainLayout,
    children: [
      { path: '', pathMatch: 'full', redirectTo: APP_ROUTES.AUTH.LOGIN },
      {
        path: APP_ROUTES.AUTH.REGISTER.ROOT,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@taxora-business-auth/feature/create-account-page/create-account-page'),
          },
          {
            path: APP_ROUTES.AUTH.REGISTER.SUCCESS,
            canActivate: [checkEmailGuard],
            data: {
              emailSelector: AuthState.createUserEmail,
            },
            loadComponent: () =>
              import('@taxora-business-auth/feature/create-account-success-page/create-account-success-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.ACTIVATE.ROOT,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: APP_ROUTES.AUTH.ACTIVATE.TOKEN,
          },
          {
            path: APP_ROUTES.AUTH.ACTIVATE.SUCCESS,
            loadComponent: () =>
              import('@taxora-business-auth/feature/activate-account-success-page/activate-account-success-page'),
          },
          {
            path: APP_ROUTES.AUTH.ACTIVATE.TOKEN,
            loadComponent: () =>
              import('@taxora-business-auth/feature/activate-account-page/activate-account-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.RESEND_ACTIVATION.ROOT,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@taxora-business-auth/feature/resend-activation-page/resend-activation-page'),
          },
          {
            path: APP_ROUTES.AUTH.RESEND_ACTIVATION.SUCCESS,
            canActivate: [checkEmailGuard],
            data: {
              emailSelector: AuthState.resendActivationEmail,
            },
            loadComponent: () =>
              import('@taxora-business-auth/feature/resend-activation-success-page/resend-activation-success-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.LOGIN,
        loadComponent: () => import('@taxora-business-auth/feature/login-page/login-page'),
      },
      {
        path: APP_ROUTES.AUTH.FORGOT_PASSWORD.ROOT,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@taxora-business-auth/feature/forgot-password-page/forgot-password-page'),
          },
          {
            path: APP_ROUTES.AUTH.FORGOT_PASSWORD.SUCCESS,
            canActivate: [checkEmailGuard],
            data: {
              emailSelector: AuthState.forgotPasswordEmail,
            },
            loadComponent: () =>
              import('@taxora-business-auth/feature/forgot-password-success-page/forgot-password-success-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.SET_PASSWORD.ROOT,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: APP_ROUTES.AUTH.SET_PASSWORD.TOKEN,
          },
          {
            path: APP_ROUTES.AUTH.SET_PASSWORD.SUCCESS,
            loadComponent: () =>
              import('@taxora-business-auth/feature/set-password-success-page/set-password-success-page'),
          },
          {
            path: APP_ROUTES.AUTH.SET_PASSWORD.TOKEN,
            loadComponent: () =>
              import('@taxora-business-auth/feature/set-password-page/set-password-page'),
          },
        ],
      },
      { path: '**', redirectTo: APP_ROUTES.AUTH.LOGIN },
    ],
  },
  { path: '**', redirectTo: APP_ROUTES.DASHBOARD },
];
